import AddIcon from '@mui/icons-material/Add'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText
} from '@mui/material'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { getVisibleNavItems } from '../nav-access'
import { SidebarItem } from './SidebarItem'
import { SidebarPopout } from './SidebarPopout'

export function SidebarSection({
    section,
    collapsed,
    navAccess,
    open,
    onToggle,
    onCreateClick
}) {
    const [popoutAnchorEl, setPopoutAnchorEl] = useState(null)
    const iconRef = useRef(null)
    const closeTimerRef = useRef(null)

    useEffect(() => () => clearTimeout(closeTimerRef.current), [])

    const scheduleClose = () => {
        closeTimerRef.current = setTimeout(() => setPopoutAnchorEl(null), 200)
    }
    const cancelClose = () => clearTimeout(closeTimerRef.current)

    const visibleItems = getVisibleNavItems(section.items, navAccess)

    const handleIconMouseEnter = () => {
        if (collapsed) {
            cancelClose()
            setPopoutAnchorEl(iconRef.current)
        }
    }
    const handleIconMouseLeave = () => {
        if (collapsed) scheduleClose()
    }
    const handleIconClick = () => {
        if (collapsed) setPopoutAnchorEl(iconRef.current)
        else onToggle()
    }

    if (collapsed) {
        return (
            <>
                <ListItemButton
                    ref={iconRef}
                    onClick={handleIconClick}
                    onMouseEnter={handleIconMouseEnter}
                    onMouseLeave={handleIconMouseLeave}
                    sx={{
                        minHeight: 48,
                        px: 2.5,
                        justifyContent: 'center'
                    }}
                    aria-label={section.label}
                >
                    <ListItemIcon
                        sx={{ minWidth: 0, justifyContent: 'center' }}
                    >
                        {section.icon}
                    </ListItemIcon>
                </ListItemButton>
                <SidebarPopout
                    anchorEl={popoutAnchorEl}
                    open={!!popoutAnchorEl}
                    onClose={() => setPopoutAnchorEl(null)}
                    section={section}
                    navAccess={navAccess}
                    onMouseEnter={cancelClose}
                    onMouseLeave={() => setPopoutAnchorEl(null)}
                    onCreateClick={onCreateClick}
                />
            </>
        )
    }

    return (
        <>
            <ListItemButton
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`section-${section.id}-items`}
                sx={{ minHeight: 48, px: 2.5 }}
            >
                <ListItemIcon
                    sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}
                >
                    {section.icon}
                </ListItemIcon>
                <ListItemText primary={section.label} />
                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={open} timeout={300} unmountOnExit={false}>
                <List
                    id={`section-${section.id}-items`}
                    disablePadding
                    component="div"
                >
                    {visibleItems.map((item) => (
                        <SidebarItem
                            key={item.id}
                            item={{
                                ...item,
                                icon: item.icon ?? section.icon
                            }}
                            collapsed={false}
                            sx={{ pl: 4 }}
                        />
                    ))}

                    {/* "+ Create" button at bottom of section */}
                    {(section.createModal || section.createPath) &&
                        navAccess.isAuthenticated && (
                        <ListItemButton
                            {...(section.createModal
                                ? { component: 'button', onClick: () => onCreateClick(section) }
                                : { component: Link, to: section.createPath })}
                            sx={{ pl: 4, py: 0.5, width: '100%' }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                                <AddIcon />
                            </ListItemIcon>
                            <ListItemText primary="Create" />
                        </ListItemButton>
                    )}
                </List>
            </Collapse>
        </>
    )
}

SidebarSection.propTypes = {
    section: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
                path: PropTypes.string.isRequired,
                icon: PropTypes.element,
                authRequired: PropTypes.bool.isRequired,
                matchSubpaths: PropTypes.bool
            })
        ).isRequired,
        createPath: PropTypes.string,
        createModal: PropTypes.bool,
        discoverPath: PropTypes.string.isRequired,
        pinToTop: PropTypes.bool
    }).isRequired,
    collapsed: PropTypes.bool.isRequired,
    navAccess: PropTypes.shape({
        isAuthenticated: PropTypes.bool.isRequired,
        isStaff: PropTypes.bool.isRequired,
        user: PropTypes.object
    }).isRequired,
    open: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onCreateClick: PropTypes.func
}
